import React, { useState, useContext } from 'react';
import { IcheerContext } from '../Context/AppContext'; // Adjust path as needed
import { UserPlus, Mail, Send, Activity, Eye, EyeOff, Users, ArrowLeft, MapPin, Tag, User, Building, UserCheck } from 'lucide-react';
import { useNavigate } from "react-router-dom";
import 'bootstrap/dist/css/bootstrap.min.css';
import Logo from '../components/Logo';

const RegisterPage = () => {
  const { host, setIsAdminLoggedIn } = useContext(IcheerContext);
  const navigate = useNavigate();

  const [step, setStep] = useState(1); // 1: Registration Type & Email, 2: Register with details
  const [formData, setFormData] = useState({
    email: '',
    registrationType: 'team',
    Name: '',
    firstName: '',
    lastName: '',
    promocode: '',
    street_address: '',   // ← Renamed
    city: '',             // ← NEW
    state: '',            // ← NEW
    zip_code: '',         // ← NEW
    teamType: '',
    teamSize: '',
    password: '',
    confirmPassword: '',
    frontendOtp: '',
    brand_ambassador_code: ''  // ← NEW (only for individual)
  });
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const registrationTypes = [
    { value: 'team', label: 'Team', icon: Users, col: 'col-md-3' },
    { value: 'gym', label: 'Gym', icon: Building, col: 'col-md-3' },
    { value: 'individual', label: 'Individual Cheerleader', icon: UserCheck, col: 'col-md-6' }
  ];

  const teamTypeOptions = [
    'Elementary',
    'Middle',
    'High School',
    'College UCA',
    'College NCA',
    'All Star',
    'Recreational'
  ];

  const sizeOptions = [
    '0–50 Cheerleaders',
    '50–100 Cheerleaders',
    '100+ Cheerleaders'
  ];

  const handleInputChange = (e) => {
    let { name, value } = e.target;

    // For promocode only: remove spaces
    if (name === "promocode") {
      value = value.replace(/\s+/g, ""); // remove all spaces
    }

    setFormData({
      ...formData,
      [name]: value
    });
  };

  const requestOTP = async (email, registrationType) => {
    const response = await fetch(`${host}team/request-otp`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, registration_type: registrationType }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Request failed');
    }

    return response.json();
  };

  const createTeam = async (teamData) => {
    const response = await fetch(`${host}team/create`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(teamData),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Registration failed');
    }

    return response.json();
  };

  const handleRequestOTP = async () => {
    if (!formData.email || !formData.registrationType) {
      setMessage('Please enter your email and select registration type');
      setMessageType('danger');
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setMessage('Please enter a valid email address');
      setMessageType('danger');
      return;
    }

    setLoading(true);
    try {
      const response = await requestOTP(formData.email, formData.registrationType);
      setMessage('OTP sent to your email successfully!');
      setMessageType('success');
      setOtp(response.otp); // In production, don't show OTP in response
      setStep(2);
    } catch (error) {
      setMessage(error.message || 'Failed to send OTP');
      setMessageType('danger');
    }
    setLoading(false);
  };

  const handleRegister = async () => {
    // Validate required fields based on registration type
    const requiredFields = ['Name', 'firstName', 'lastName', 'promocode', 'street_address', 'city', 'state', 'zip_code', 'password', 'confirmPassword', 'frontendOtp'];

    if (formData.registrationType === 'team') {
      requiredFields.push('teamType', 'teamSize');
    } else if (formData.registrationType === 'gym') {
      requiredFields.push('teamSize'); // Gym size
    } else if (formData.registrationType === 'individual') {
      // requiredFields.push('brand_ambassador_code'); // Only for individual
    }

    const missingFields = requiredFields.filter(field => !formData[field]);
    if (missingFields.length > 0) {
      setMessage('Please fill in all fields');
      setMessageType('danger');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setMessage('Passwords do not match');
      setMessageType('danger');
      return;
    }

    if (formData.password.length < 6) {
      setMessage('Password must be at least 6 characters long');
      setMessageType('danger');
      return;
    }

    if (formData.frontendOtp.length < 6) {
      setMessage('OTP must be at least 6 characters long');
      setMessageType('danger');
      return;
    }
    if (formData.promocode.length < 6) {
      setMessage('Referral code must be at least 6 characters long');
      setMessageType('danger');
      return;
    }

    if (formData.frontendOtp !== otp) {
      setMessage('Wrong OTP');
      setMessageType('danger');
      return;
    }

    setLoading(true);
    try {
      const teamData = {
        Name: formData.Name,
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        password: formData.password,
        promocode: formData.promocode,
        street_address: formData.street_address,  // ← Updated
        city: formData.city,                      // ← NEW
        state: formData.state,                    // ← NEW
        zip_code: formData.zip_code,              // ← NEW
        team_type: formData.teamType || 'N/A',
        team_size: formData.teamSize || 'N/A',
        registration_type: formData.registrationType,
        brand_ambassador_code: formData.brand_ambassador_code || null  // ← Only sent if present
      };

      const response = await createTeam(teamData);
      setMessage(`Registration successful! Please wait for the admin to accept your ${formData.registrationType} account before you can log in.`);
      setMessageType('success');
    } catch (error) {
      setMessage(error.message || 'Registration failed');
      setMessageType('danger');
    }
    setLoading(false);
  };

  const handleKeyPressStep1 = (e) => {
    if (e.key === 'Enter') {
      handleRequestOTP();
    }
  };

  const handleKeyPressStep2 = (e) => {
    if (e.key === 'Enter') {
      handleRegister();
    }
  };

  const getFieldLabel = (baseLabel) => {
    if (formData.registrationType === 'gym') {
      return baseLabel.replace('Team', 'Gym');
    }
    return baseLabel;
  };

  const getNamePlaceholder = () => {
    switch (formData.registrationType) {
      case 'team': return 'Enter your team name';
      case 'gym': return 'Enter your gym name';
      case 'individual': return 'Enter your gym/team name';
      default: return 'Enter name';
    }
  };

  const getSizePlaceholder = () => {
    switch (formData.registrationType) {
      case 'team': return 'Select team size';
      case 'gym': return 'Select gym size';
      default: return 'Select size';
    }
  };

  const renderRegistrationTypeSelector = () => (
    <div className="mb-4">
      <label className="form-label text-dark fw-semibold mb-3">
        <UserCheck size={16} className="me-2" />
        Registration Type
      </label>
      <div className="row g-3">
        {registrationTypes.map((type) => {
          const IconComponent = type.icon;
          return (
            <div key={type.value} className={type.col}>
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="radio"
                  name="registrationType"
                  id={type.value}
                  value={type.value}
                  checked={formData.registrationType === type.value}
                  onChange={handleInputChange}
                  style={{ transform: 'scale(1.2)' }}
                />
                <label
                  className="form-check-label w-100 p-2 border rounded"
                  htmlFor={type.value}
                  style={{
                    cursor: 'pointer',
                    backgroundColor: formData.registrationType === type.value ? '#f0f8ff' : '#f8f9fa',
                    borderColor: formData.registrationType === type.value ? '#667eea' : '#dee2e6',
                    transition: 'all 0.2s ease'
                  }}
                >
                  <div className="d-flex align-items-center">
                    <IconComponent size={20} className="me-3 text-primary" />
                    <span className="fw-semibold">{type.label}</span>
                  </div>
                </label>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className="min-vh-100 d-flex align-items-center" style={{
      background: '#dc59a0'
    }}>
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-md-6">
            {/* Header */}
            <div className="text-center mb-4">
              <Logo />
            </div>

            {/* Register Card */}
            <div className="card shadow-lg border-0" style={{ borderRadius: '15px' }}>
              <div className="card-header bg-white border-0 text-center py-4" style={{ borderRadius: '15px 15px 0 0' }}>
                <h4 className="mb-0 text-dark fw-bold">Create Your Account</h4>
                <p className="text-muted mb-0 mt-1">
                  {step === 1 ? 'Step 1 of 2 - Registration Type & Email' : 'Step 2 of 2 - Account Details'}
                </p>
              </div>

              <div className="card-body p-4">
                {step === 1 ? (
                  <div>
                    <div className="text-center mb-4">
                      <div className="bg-primary bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
                        style={{ width: '60px', height: '60px' }}>
                        <Mail size={24} className="text-primary" />
                      </div>
                      <p className="text-muted">Choose your registration type and enter your email</p>
                    </div>

                    {renderRegistrationTypeSelector()}

                    <div className="mb-4">
                      <label htmlFor="email" className="form-label text-dark fw-semibold">
                        <Mail size={16} className="me-2" />
                        Email Address
                      </label>
                      <input
                        type="email"
                        className="form-control form-control-lg border-0 shadow-sm"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        onKeyPress={handleKeyPressStep1}
                        placeholder="Enter your email address"
                        style={{
                          backgroundColor: '#f8f9fa',
                          borderRadius: '10px',
                          padding: '12px 16px'
                        }}
                      />
                    </div>

                    {message && (
                      <div className={`alert alert-${messageType} alert-dismissible border-0 shadow-sm mb-4`} role="alert">
                        <div className="d-flex align-items-center">
                          <div className="flex-grow-1">{message}</div>
                          <button
                            type="button"
                            className="btn-close"
                            onClick={() => setMessage('')}
                          ></button>
                        </div>
                      </div>
                    )}

                    <button
                      type="button"
                      className="btn btn-primary btn-lg w-100 fw-semibold shadow-sm mb-3"
                      onClick={handleRequestOTP}
                      disabled={loading}
                      style={{
                        borderRadius: '10px',
                        padding: '12px',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        border: 'none'
                      }}
                    >
                      {loading ? (
                        <div className="d-flex align-items-center justify-content-center">
                          <div className="spinner-border spinner-border-sm me-2" role="status">
                            <span className="visually-hidden">Loading...</span>
                          </div>
                          Sending verification code...
                        </div>
                      ) : (
                        <div className="d-flex align-items-center justify-content-center">
                          <Send size={18} className="me-2" />
                          Send Verification Code
                        </div>
                      )}
                    </button>
                  </div>
                ) : (
                  <div>
                    <div className="text-center mb-4">
                      <div className="bg-success bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
                        style={{ width: '60px', height: '60px' }}>
                        {formData.registrationType === 'team' && <Users size={24} className="text-success" />}
                        {formData.registrationType === 'gym' && <Building size={24} className="text-success" />}
                        {formData.registrationType === 'individual' && <UserCheck size={24} className="text-success" />}
                      </div>
                      <p className="text-muted">Complete your {formData.registrationType} registration</p>
                    </div>

                    <div className="mb-3">
                      <label htmlFor="email" className="form-label text-dark fw-semibold">
                        <Mail size={16} className="me-2" />
                        Email Address
                      </label>
                      <input
                        type="email"
                        className="form-control border-0 shadow-sm"
                        value={formData.email}
                        disabled
                        style={{
                          backgroundColor: '#e9ecef',
                          borderRadius: '8px'
                        }}
                      />
                    </div>

                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <label htmlFor="firstName" className="form-label text-dark fw-semibold">
                          <User size={16} className="me-2" />
                          First Name
                        </label>
                        <input
                          type="text"
                          className="form-control form-control-lg border-0 shadow-sm"
                          id="firstName"
                          name="firstName"
                          value={formData.firstName}
                          onChange={handleInputChange}
                          onKeyPress={handleKeyPressStep2}
                          placeholder="Enter your first name"
                          style={{
                            backgroundColor: '#f8f9fa',
                            borderRadius: '10px',
                            padding: '12px 16px'
                          }}
                        />
                      </div>
                      <div className="col-md-6 mb-3">
                        <label htmlFor="lastName" className="form-label text-dark fw-semibold">
                          <User size={16} className="me-2" />
                          Last Name
                        </label>
                        <input
                          type="text"
                          className="form-control form-control-lg border-0 shadow-sm"
                          id="lastName"
                          name="lastName"
                          value={formData.lastName}
                          onChange={handleInputChange}
                          onKeyPress={handleKeyPressStep2}
                          placeholder="Enter your last name"
                          style={{
                            backgroundColor: '#f8f9fa',
                            borderRadius: '10px',
                            padding: '12px 16px'
                          }}
                        />
                      </div>
                    </div>

                    <div className="mb-3">
                      <label htmlFor="Name" className="form-label text-dark fw-semibold">
                        {formData.registrationType === 'team' && <Users size={16} className="me-2" />}
                        {formData.registrationType === 'gym' && <Building size={16} className="me-2" />}
                        {formData.registrationType === 'individual' && <UserCheck size={16} className="me-2" />}
                        {formData.registrationType === 'team' && 'Team Name'}
                        {formData.registrationType === 'gym' && 'Gym Name'}
                        {formData.registrationType === 'individual' && 'Gym / Team Name'}
                      </label>
                      <input
                        type="text"
                        className="form-control form-control-lg border-0 shadow-sm"
                        id="Name"
                        name="Name"
                        value={formData.Name}
                        onChange={handleInputChange}
                        onKeyPress={handleKeyPressStep2}
                        placeholder={getNamePlaceholder()}
                        style={{
                          backgroundColor: '#f8f9fa',
                          borderRadius: '10px',
                          padding: '12px 16px'
                        }}
                      />
                    </div>

                    <div className="mb-3">
                      <label htmlFor="promocode" className="form-label text-dark fw-semibold">
                        <Tag size={16} className="me-2" />
                        Referral Code
                      </label>
                      <input
                        type="text"
                        className="form-control form-control-lg border-0 shadow-sm"
                        id="promocode"
                        name="promocode"
                        value={formData.promocode}
                        onChange={handleInputChange}
                        onKeyPress={handleKeyPressStep2}
                        placeholder="Create your referral code"
                        minLength={6}
                        required
                        style={{
                          backgroundColor: '#f8f9fa',
                          borderRadius: '10px',
                          padding: '12px 16px'
                        }}
                      />
                      <small className="text-muted" style={{ fontSize: '0.8rem' }}>
                        Minimum 6 characters
                      </small>
                    </div>

                    {/* Address Fields */}
                    <div className="mb-3">
                      <label htmlFor="street_address" className="form-label text-dark fw-semibold">
                        <MapPin size={16} className="me-2" />
                        Street Address
                      </label>
                      <textarea
                        className="form-control form-control-lg border-0 shadow-sm"
                        id="street_address"
                        name="street_address"
                        value={formData.street_address}
                        onChange={handleInputChange}
                        onKeyPress={handleKeyPressStep2}
                        placeholder="Enter your street address"
                        style={{
                          backgroundColor: '#f8f9fa',
                          borderRadius: '10px',
                          padding: '12px 16px',
                          resize: 'vertical',
                          minHeight: '100px'
                        }}
                      />
                    </div>

                    <div className="row">
                      <div className="col-md-4 mb-3">
                        <label htmlFor="city" className="form-label text-dark fw-semibold">
                          City
                        </label>
                        <input
                          type="text"
                          className="form-control form-control-lg border-0 shadow-sm"
                          id="city"
                          name="city"
                          value={formData.city}
                          onChange={handleInputChange}
                          onKeyPress={handleKeyPressStep2}
                          placeholder="City"
                          style={{
                            backgroundColor: '#f8f9fa',
                            borderRadius: '10px',
                            padding: '12px 16px'
                          }}
                        />
                      </div>
                      <div className="col-md-4 mb-3">
                        <label htmlFor="state" className="form-label text-dark fw-semibold">
                          State
                        </label>
                        <input
                          type="text"
                          className="form-control form-control-lg border-0 shadow-sm"
                          id="state"
                          name="state"
                          value={formData.state}
                          onChange={handleInputChange}
                          onKeyPress={handleKeyPressStep2}
                          placeholder="State"
                          style={{
                            backgroundColor: '#f8f9fa',
                            borderRadius: '10px',
                            padding: '12px 16px'
                          }}
                        />
                      </div>
                      <div className="col-md-4 mb-3">
                        <label htmlFor="zip_code" className="form-label text-dark fw-semibold">
                          Zip Code
                        </label>
                        <input
                          type="text"
                          className="form-control form-control-lg border-0 shadow-sm"
                          id="zip_code"
                          name="zip_code"
                          value={formData.zip_code}
                          onChange={handleInputChange}
                          onKeyPress={handleKeyPressStep2}
                          placeholder="Zip Code"
                          style={{
                            backgroundColor: '#f8f9fa',
                            borderRadius: '10px',
                            padding: '12px 16px'
                          }}
                        />
                      </div>
                    </div>

                    {/* Brand Ambassador Code - Only for Individual */}
                    <div className="mb-3">
                      <label htmlFor="brand_ambassador_code" className="form-label text-dark fw-semibold">
                        <Tag size={16} className="me-2" />
                        Brand Ambassador Code
                      </label>
                      <input
                        type="text"
                        className="form-control form-control-lg border-0 shadow-sm"
                        id="brand_ambassador_code"
                        name="brand_ambassador_code"
                        value={formData.brand_ambassador_code}
                        onChange={handleInputChange}
                        onKeyPress={handleKeyPressStep2}
                        placeholder="Enter ambassador code (if provided)"
                        style={{
                          backgroundColor: '#f8f9fa',
                          borderRadius: '10px',
                          padding: '12px 16px'
                        }}
                      />
                      <small className="text-muted" style={{ fontSize: '0.8rem' }}>
                        Did one of our Brand Ambassador Captains help you sign up?
                      </small>
                    </div>

                    {/* Team Type - only for teams */}
                    {formData.registrationType === 'team' && (
                      <div className="mb-3">
                        <label htmlFor="teamType" className="form-label text-dark fw-semibold">
                          <Users size={16} className="me-2" />
                          Team Type
                        </label>
                        <select
                          className="form-control form-control-lg border-0 shadow-sm"
                          id="teamType"
                          name="teamType"
                          value={formData.teamType}
                          onChange={handleInputChange}
                          onKeyPress={handleKeyPressStep2}
                          style={{
                            backgroundColor: '#f8f9fa',
                            borderRadius: '10px',
                            padding: '12px 16px'
                          }}
                        >
                          <option value="" disabled>Select team type</option>
                          {teamTypeOptions.map((option) => (
                            <option key={option} value={option}>{option}</option>
                          ))}
                        </select>
                      </div>
                    )}

                    {/* Size - for teams and gyms */}
                    {(formData.registrationType === 'team' || formData.registrationType === 'gym') && (
                      <div className="mb-3">
                        <label htmlFor="teamSize" className="form-label text-dark fw-semibold">
                          {formData.registrationType === 'team' && <Users size={16} className="me-2" />}
                          {formData.registrationType === 'gym' && <Building size={16} className="me-2" />}
                          {getFieldLabel('Team Size')}
                        </label>
                        <select
                          className="form-control form-control-lg border-0 shadow-sm"
                          id="teamSize"
                          name="teamSize"
                          value={formData.teamSize}
                          onChange={handleInputChange}
                          onKeyPress={handleKeyPressStep2}
                          style={{
                            backgroundColor: '#f8f9fa',
                            borderRadius: '10px',
                            padding: '12px 16px'
                          }}
                        >
                          <option value="" disabled>{getSizePlaceholder()}</option>
                          {sizeOptions.map((option) => (
                            <option key={option} value={option}>{option}</option>
                          ))}
                        </select>
                      </div>
                    )}

                    <div className="mb-3">
                      <label htmlFor="password" className="form-label text-dark fw-semibold">
                        <User size={16} className="me-2" />
                        Password
                      </label>
                      <div className="position-relative">
                        <input
                          type={showPassword ? 'text' : 'password'}
                          className="form-control form-control-lg border-0 shadow-sm"
                          id="password"
                          name="password"
                          value={formData.password}
                          onChange={handleInputChange}
                          onKeyPress={handleKeyPressStep2}
                          placeholder="Enter your password"
                          style={{
                            backgroundColor: '#f8f9fa',
                            borderRadius: '10px',
                            padding: '12px 45px 12px 16px'
                          }}
                        />
                        <button
                          type="button"
                          className="btn btn-link position-absolute end-0 top-50 translate-middle-y me-2"
                          onClick={() => setShowPassword(!showPassword)}
                          style={{ border: 'none', background: 'none' }}
                        >
                          {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                      </div>
                      <small className="text-muted" style={{ fontSize: '0.8rem' }}>
                        Minimum 6 characters
                      </small>
                    </div>

                    <div className="mb-3">
                      <label htmlFor="confirmPassword" className="form-label text-dark fw-semibold">
                        <User size={16} className="me-2" />
                        Confirm Password
                      </label>
                      <div className="position-relative">
                        <input
                          type={showConfirmPassword ? 'text' : 'password'}
                          className="form-control form-control-lg border-0 shadow-sm"
                          id="confirmPassword"
                          name="confirmPassword"
                          value={formData.confirmPassword}
                          onChange={handleInputChange}
                          onKeyPress={handleKeyPressStep2}
                          placeholder="Confirm your password"
                          style={{
                            backgroundColor: '#f8f9fa',
                            borderRadius: '10px',
                            padding: '12px 45px 12px 16px'
                          }}
                        />
                        <button
                          type="button"
                          className="btn btn-link position-absolute end-0 top-50 translate-middle-y me-2"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          style={{ border: 'none', background: 'none' }}
                        >
                          {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                      </div>
                    </div>

                    <div className="mb-3">
                      <label htmlFor="frontendOtp" className="form-label text-dark fw-semibold">
                        <Activity size={16} className="me-2" />
                        OTP (One-Time Password)
                      </label>
                      <input
                        type="text"
                        className="form-control form-control-lg border-0 shadow-sm"
                        id="frontendOtp"
                        name="frontendOtp"
                        value={formData.frontendOtp}
                        onChange={handleInputChange}
                        onKeyPress={handleKeyPressStep2}
                        placeholder="Enter your OTP"
                        style={{
                          backgroundColor: '#f8f9fa',
                          borderRadius: '10px',
                          padding: '12px 16px'
                        }}
                      />
                    </div>

                    {message && (
                      <div className={`alert alert-${messageType} alert-dismissible border-0 shadow-sm mb-4`} role="alert">
                        <div className="d-flex align-items-center">
                          <div className="flex-grow-1">{message}</div>
                          <button
                            type="button"
                            className="btn-close"
                            onClick={() => setMessage('')}
                          ></button>
                        </div>
                      </div>
                    )}

                    <button
                      type="button"
                      className="btn btn-success btn-lg w-100 fw-semibold shadow-sm mb-3"
                      onClick={handleRegister}
                      disabled={loading}
                      style={{
                        borderRadius: '10px',
                        padding: '12px',
                        background: 'linear-gradient(135deg, #28a745 0%, #20c997 100%)',
                        border: 'none'
                      }}
                    >
                      {loading ? (
                        <div className="d-flex align-items-center justify-content-center">
                          <div className="spinner-border spinner-border-sm me-2" role="status">
                            <span className="visually-hidden">Loading...</span>
                          </div>
                          Creating your account...
                        </div>
                      ) : (
                        <div className="d-flex align-items-center justify-content-center">
                          <UserPlus size={18} className="me-2" />
                          Create My Account
                        </div>
                      )}
                    </button>

                    <button
                      type="button"
                      className="btn btn-outline-secondary w-100"
                      onClick={() => setStep(1)}
                      disabled={loading}
                      style={{ borderRadius: '8px' }}
                    >
                      <ArrowLeft size={16} className="me-2" />
                      Back to Registration Type
                    </button>
                  </div>
                )}

                <div className="text-center mt-4">
                  <p className="text-muted mb-0">
                    Already have an account?{' '}
                    <a href="/login" className="text-decoration-none fw-semibold"
                      style={{ color: '#667eea' }}>
                      Sign in here
                    </a>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .form-check{
          display:flex;
          gap:10px;
          align-items:center;
        }
      `}</style>
    </div>
  );
};

export default RegisterPage;